/**
 * wine-drawer.js — Tasting notes / food pairings drawer (base feature).
 *
 * STAGE: Base — loads for ALL wineries.
 * Parity: RedConcierge/static/javascript/main.js (openTastingNotesDrawer,
 *   loadFoodPairingsForDrawer, showRecipeDetail, downloadRecipeAsPDF).
 *
 * EVENTS:
 *   in:  ws-wine-drawer-open { uniqueId, wineKey, variantId, foodPairingsTaskId, ... }
 *        ws-cart-count (footer sync when drawer open)
 *   out: ws-cart-add, ws-cart-update, ws-cart-remove (footer actions)
 */

import * as api from "../../core/api.js";
import { on, emit, getState } from "../../core/state.js";
import { getUiFeature, getTheme, getTenantId, getFeature } from "../../core/configAccessors.js";
import { ensureSession } from "./chat-handler.js";
import wineDrawerCSS from "../../styles/wine-drawer.css";
import recipePrintTemplate from "../../assets/templates/recipe_print_template.js";
import redAugmentLogo from "../../assets/images/red-augment-logo.png";
import { brandWordmarkHtml } from "../../components/base/BrandWordmark.js";
import wineEngineLogoImg from "../../assets/images/wineengine-logo.png";

const ICON_TASTING = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 18 22" fill="none">
                          <path d="M10.75 0.75V4.75C10.75 5.28043 10.9607 5.78914 11.3358 6.16421C11.7109 6.53929 12.2196 6.75 12.75 6.75H16.75M6.75 7.75H4.75M12.75 11.75H4.75M12.75 15.75H4.75M11.75 0.75H2.75C2.21957 0.75 1.71086 0.960714 1.33579 1.33579C0.960714 1.71086 0.75 2.21957 0.75 2.75V18.75C0.75 19.2804 0.960714 19.7891 1.33579 20.1642C1.71086 20.5393 2.21957 20.75 2.75 20.75H14.75C15.2804 20.75 15.7891 20.5393 16.1642 20.1642C16.5393 19.7891 16.75 19.2804 16.75 18.75V5.75L11.75 0.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>`;
const ICON_FOOD = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 20 20" fill="none">
                          <path d="M18.948 9.47468H16.8429V6.84252C16.8429 4.69989 15.2322 2.92638 13.1588 2.66867V2.63215C13.1588 2.05282 12.685 1.57909 12.1057 1.57909H6.82659C6.1999 0.595128 5.12614 0 3.94765 0H0.526017C0.236859 0 0 0.236863 0 0.526027C0 0.81519 0.236859 1.05205 0.526017 1.05205H3.94765C4.83685 1.05205 5.64811 1.5465 6.05273 2.34691C6.14254 2.52061 6.32117 2.63114 6.52152 2.63114H9.47449V4.73626H6.52152C6.32118 4.73626 6.14254 4.8468 6.05273 5.0205C5.77342 5.57318 5.29478 5.97288 4.73718 6.17323V3.15704C4.73718 2.57771 4.26346 2.10398 3.68414 2.10398H0.526017C0.236859 2.10398 0 2.34084 0 2.63001C0 2.91917 0.236859 3.15603 0.526017 3.15603H3.68414V6.31421H0.526017C0.236859 6.31421 0 6.55108 0 6.84024C0 7.1294 0.236859 7.36627 0.526017 7.36627H3.94765C5.12702 7.36627 6.20078 6.77114 6.82659 5.78718H12.1057C12.4689 5.78718 12.7896 5.60262 12.9791 5.32431C13.3897 5.46643 13.6848 5.85628 13.6848 6.31421V9.47239C13.1054 9.47239 12.6317 9.94612 12.6317 10.5255V13.1576C12.6317 15.0101 14.0055 16.5419 15.7898 16.8004V18.9479H14.7368C14.4476 18.9479 14.2108 19.1848 14.2108 19.474C14.2108 19.7631 14.4476 20 14.7368 20H17.8949C18.1841 20 18.4209 19.7631 18.4209 19.474C18.4209 19.1848 18.1841 18.9479 17.8949 18.9479H16.8419V16.8004C18.6262 16.5428 20 15.0111 20 13.1576V10.5255C20 9.94612 19.5263 9.47239 18.947 9.47239L18.948 9.47468ZM10.5256 4.73741V2.63229H12.1047V4.73741H10.5256ZM13.1577 4.2844V3.73171C14.647 3.98437 15.7898 5.27922 15.7898 6.8424V9.47455H14.7368V6.31637C14.7368 5.33733 14.0627 4.52126 13.1577 4.2844ZM18.948 10.5267V11.5798H13.6848V10.5267H18.948ZM16.3159 15.791C14.8631 15.791 13.6838 14.6116 13.6838 13.1589V12.6329H18.947V13.1589C18.947 14.6116 17.7676 15.791 16.3149 15.791H16.3159Z" fill="currentColor"/>
                          </svg>`;

const ICON_CLOSE = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

const TRASH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="#0A0A0A" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** @type {HTMLElement|null} */
let drawerEl = null;
/** @type {object} */
let drawerState = {};

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function fpCacheKey(taskId, wineKey) {
  return `ws_fp:${taskId}:${wineKey}`;
}

function fpCacheGet(taskId, wineKey) {
  try {
    const raw = sessionStorage.getItem(fpCacheKey(taskId, wineKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function fpCacheSet(taskId, wineKey, data) {
  try {
    sessionStorage.setItem(fpCacheKey(taskId, wineKey), JSON.stringify(data));
  } catch {
    /* quota */
  }
}

function normalizeTastingNotes(rawNotes) {
  if (!rawNotes) return {};
  if (typeof rawNotes === "object") return rawNotes;
  if (typeof rawNotes !== "string") return {};
  const trimmed = rawNotes.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "object" && parsed ? parsed : { Description: trimmed };
  } catch {
    return { Description: trimmed };
  }
}

function formatLabel(label) {
  return String(label ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Parity: old getWineDrawerDetails + winemaker_notes fallbacks. */
function getWineDrawerDetails(wine) {
  const notes = wine.winemaker_notes || {};
  const wd = wine.wine_details || {};
  const appellation = notes.Appellation || wd.region || "";
  const country = wine.country || "";
  const region =
    wd.region ||
    (appellation && country ? `${appellation}, ${country}` : appellation || country);

  const details = {
    ...wd,
    composition: wd.composition || wine.composition || "",
    vintage: wd.vintage || notes.Vintage || "",
    region,
    vineyard: wd.vineyard || wine.maker || "",
  };

  if (typeof wine.tasting_notes === "string" && wine.tasting_notes.trim()) {
    details.nose = details.nose || wine.tasting_notes.trim();
  } else if (wine.tasting_notes && typeof wine.tasting_notes === "object") {
    Object.assign(details, wine.tasting_notes);
  }

  return details;
}

function buildDrawerNotesPayload(wine) {
  const details = getWineDrawerDetails(wine);
  const payload = { ...details };

  if (wine.description) {
    payload.overall = wine.description;
  }

  const hasContent = Object.values(payload).some((v) => v != null && v !== "");
  if (!hasContent && typeof wine.tasting_notes === "string" && wine.tasting_notes.trim()) {
    payload.overall = wine.tasting_notes.trim();
  }

  return payload;
}

function buildTastingPaneHtml(notes) {
  const normalized = normalizeTastingNotes(notes);
  const introKeys = ["overall", "intro", "description", "summary", "overview"];
  const sectionKeys = ["nose", "palate", "region", "vineyard", "vintage", "composition"];
  let intro = "";
  const sections = [];
  const used = new Set();

  Object.entries(normalized).forEach(([key, value]) => {
    if (!value) return;
    const k = String(key).toLowerCase();
    if (!intro && introKeys.includes(k)) {
      intro = value;
      used.add(k);
    }
  });

  sectionKeys.forEach((sk) => {
    const entry = Object.entries(normalized).find(([k]) => String(k).toLowerCase() === sk);
    if (!entry) return;
    const [key, value] = entry;
    if (!value || used.has(sk)) return;
    sections.push({ label: key, value });
    used.add(sk);
  });

  if (!intro && !sections.length) return "";

  let html = intro ? `<div class="ws-wine-drawer-intro">${escapeHtml(intro)}</div>` : "";
  sections.forEach((s) => {
    html += `<h4>${escapeHtml(formatLabel(s.label))}</h4><p>${escapeHtml(s.value)}</p>`;
  });
  return html;
}

function allPairings(data) {
  const current = data?.pairings || [];
  const previous = data?._previousPairings || [];
  return [...current, ...previous];
}

/** Recipe payload ready to render. */
function isRecipeReady(data) {
  if (!data || typeof data !== "object") return false;
  if (data.status === "completed") return true;
  const recipe = data.recipe;
  return !!(recipe && (recipe.ingredients?.length || recipe.instructions?.length));
}

function buildFoodPairingsHtml(data) {
  if (!data) return "";
  const blurb = data.blurb || "";
  let pairings = data.pairings || data;
  const previousPairings = data._previousPairings || [];
  if (typeof pairings === "string") {
    pairings = pairings.split(/[,;|\n]/).map((s) => s.trim()).filter(Boolean);
  }
  if (!Array.isArray(pairings) || !pairings.length) return "";

  let html = blurb
    ? `<p class="ws-drawer-fp-blurb">${escapeHtml(blurb)}</p>
       <div class="ws-drawer-fp-heading">Recipes
         <button type="button" class="ws-drawer-recipe-refresh" data-action="recipe-refresh" title="New recipes">
           <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.5 6.5C12.5 4.9087 11.8679 3.38258 10.7426 2.25736C9.61742 1.13214 8.0913 0.5 6.5 0.5C4.82263 0.50631 3.21265 1.16082 2.00667 2.32667L0.5 3.83333M3.83333 3.83333H0.5V0.5M0.5 6.5C0.5 8.0913 1.13214 9.61742 2.25736 10.7426C3.38258 11.8679 4.9087 12.5 6.5 12.5C8.17737 12.4937 9.78735 11.8392 10.9933 10.6733L12.5 9.16667M12.5 12.5V9.16667H9.16667" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"/></svg>
           New Recipes
         </button>
       </div>`
    : "";

  html += `<div class="ws-drawer-fp-grid">`;
  pairings.forEach((item, idx) => {
    if (typeof item === "object" && item.title) {
      const img = item.image_url
        ? `<img class="ws-drawer-fp-card-img" src="${escapeHtml(item.image_url)}" alt="" loading="lazy">`
        : "";
      html += `<div class="ws-drawer-fp-card" data-pairing-index="${idx}">${img}<h4>${escapeHtml(item.title)}</h4><p>${escapeHtml(item.descriptor || "")}</p></div>`;
    } else {
      html += `<div class="ws-drawer-fp-card"><p>${escapeHtml(String(item))}</p></div>`;
    }
  });
  html += `</div>`;

  if (previousPairings.length) {
    html += `<p class="ws-drawer-fp-previous-title">Previous Recipes</p><div class="ws-drawer-fp-grid ws-drawer-fp-previous">`;
    previousPairings.forEach((item, idx) => {
      if (typeof item === "object" && item.title) {
        const img = item.image_url
          ? `<img class="ws-drawer-fp-card-img" src="${escapeHtml(item.image_url)}" alt="" loading="lazy">`
          : "";
        html += `<div class="ws-drawer-fp-card" data-pairing-index="${pairings.length + idx}">${img}<h4>${escapeHtml(item.title)}</h4><p>${escapeHtml(item.descriptor || "")}</p></div>`;
      }
    });
    html += `</div>`;
  }

  html += `<div class="ws-drawer-recipe-detail"></div>`;
  return html;
}

function loadingHtml(text = "Loading…") {
  return `<div class="ws-drawer-loading"><div class="ws-drawer-spinner"></div><p class="ws-drawer-loading-text">${escapeHtml(text)}</p></div>`;
}

function getDrawer() {
  return drawerEl;
}

function q(sel) {
  return drawerEl?.querySelector(sel) ?? null;
}

function headerIcon(tab) {
  return tab === "food-pairings" ? ICON_FOOD : ICON_TASTING;
}

function setTab(tabName) {
  const titles = { "tasting-notes": "Tasting Notes", "food-pairings": "Food Pairings" };
  drawerEl?.querySelectorAll("[data-drawer-tab]").forEach((tab) => {
    const active = tab.dataset.drawerTab === tabName;
    tab.classList.toggle("active", active);
    tab.setAttribute("aria-selected", active ? "true" : "false");
  });
  drawerEl?.querySelectorAll("[data-drawer-pane]").forEach((pane) => {
    pane.classList.toggle("active", pane.dataset.drawerPane === tabName);
  });
  const title = q("#ws-drawer-title");
  if (title) title.textContent = titles[tabName] || "Tasting Notes";
  const icon = q("#ws-drawer-header-icon");
  if (icon) icon.innerHTML = headerIcon(tabName);
}

// Commerce7 cart lines are keyed by sku (cart_c7.js expects {sku}), not
// productVariantId — same underlying identifier value as drawerState.variantId,
// just a different field name on the line and in the outgoing cart events.
function isCommerce7Tenant() {
  return getFeature("commerce7");
}

function cartLineIdKey() {
  return isCommerce7Tenant() ? "sku" : "productVariantId";
}

function cartQtyForVariant(variantId) {
  const key = cartLineIdKey();
  const line = (getState().cart?.items || []).find(
    (it) => String(it[key]) === String(variantId),
  );
  return line ? Number(line.quantity) || 0 : 0;
}

function renderFooter(variantId, qty, addLabel) {
  const footer = q(".ws-wine-drawer-footer");
  if (!footer || !variantId) return;

  let main = "";
  const showCart = getUiFeature("show_cart", false);
  if (showCart && qty > 0) {
    main = `<div class="ws-wine-drawer-qty" data-variant-id="${escapeHtml(variantId)}">
      <button type="button" class="ws-drawer-delete" data-drawer-cart="remove" data-variant-id="${escapeHtml(variantId)}" aria-label="Remove from basket">${TRASH_ICON}</button>
      <button type="button" class="ws-drawer-dec" data-drawer-cart="dec" data-variant-id="${escapeHtml(variantId)}" aria-label="Decrease quantity">−</button>
      <div class="ws-drawer-qty-val">${qty}</div>
      <button type="button" class="ws-drawer-inc" data-drawer-cart="inc" data-variant-id="${escapeHtml(variantId)}" aria-label="Increase quantity">+</button>
    </div>`;
  } else {
    main = `<button type="button" class="ws-wine-drawer-add-cart" data-drawer-cart="add" data-variant-id="${escapeHtml(variantId)}">${escapeHtml(addLabel || "Add to Basket")}</button>`;
  }

  footer.innerHTML = `${main}
    <div class="ws-wine-drawer-cancel-row">
      <span>Changed your mind?</span>
      <button type="button" class="ws-wine-drawer-cancel-btn" data-drawer-close>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M8.3 4.7L4.7 8.3M4.7 4.7L8.3 8.3M12.5 6.5C12.5 9.81371 9.81371 12.5 6.5 12.5C3.18629 12.5 0.5 9.81371 0.5 6.5C0.5 3.18629 3.18629 0.5 6.5 0.5C9.81371 0.5 12.5 3.18629 12.5 6.5Z" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"/></svg>
        Close
      </button>
    </div>`;

  const badge = q(".ws-wine-drawer-product-badge");
  if (badge) {
    if (qty > 0) {
      badge.textContent = String(qty);
      badge.style.display = "flex";
    } else {
      badge.style.display = "none";
    }
  }
}

function renderFooterCTA(ctaLink){
  const footer = q(".ws-wine-drawer-footer");
  if(!footer) return;

  footer.innerHTML = `<a href="${escapeHtml(ctaLink)}" target="_blank" class="ws-wine-drawer-add-cart" style="text-align: center;text-decoration: none;color: #000;">Buy Now</a>
    <div class="ws-wine-drawer-cancel-row">
      <span>Changed your mind?</span>
      <button type="button" class="ws-wine-drawer-cancel-btn" data-drawer-close>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M8.3 4.7L4.7 8.3M4.7 4.7L8.3 8.3M12.5 6.5C12.5 9.81371 9.81371 12.5 6.5 12.5C3.18629 12.5 0.5 9.81371 0.5 6.5C0.5 3.18629 3.18629 0.5 6.5 0.5C9.81371 0.5 12.5 3.18629 12.5 6.5Z" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"/></svg>
        Close
      </button>
    </div>`;
}

function syncFooterIfOpen() {
  if (!drawerEl?.classList.contains("is-open") || !drawerState.variantId) return;
  // Don't wipe the footer while the cart spinner overlay is showing (old kept
  // overlayLoading on the footer until the mutation finished).
  if (drawerCartBusy) return;
  renderFooter(drawerState.variantId, cartQtyForVariant(drawerState.variantId), drawerState.addToCartLabel);
}

// Parity with old overlayLoading(container) / removeOverlayLoading — spinner
// covers only .tasting-notes-footer / .ws-wine-drawer-footer.
function overlayLoading(container) {
  if (!container) return null;
  container.querySelectorAll(".ws-drawer-overlay-effect").forEach((n) => n.remove());
  const overlay = document.createElement("div");
  overlay.className = "ws-drawer-overlay-effect";
  container.style.position = "relative";
  container.appendChild(overlay);
  return overlay;
}

function removeOverlayLoading(_container, overlay) {
  overlay?.remove();
}

let drawerCartBusy = false;
let drawerFooterOverlay = null;

function setDrawerCartBusy(active) {
  drawerCartBusy = !!active && !!drawerEl?.classList.contains("is-open");
  const footer = q(".ws-wine-drawer-footer");
  if (drawerCartBusy) {
    if (!drawerFooterOverlay || !drawerFooterOverlay.isConnected) {
      drawerFooterOverlay = overlayLoading(footer);
    }
  } else {
    removeOverlayLoading(footer, drawerFooterOverlay);
    drawerFooterOverlay = null;
    // Refresh footer controls after spinner clears (qty / Add to cart).
    if (drawerEl?.classList.contains("is-open") && drawerState.variantId) {
      renderFooter(
        drawerState.variantId,
        cartQtyForVariant(drawerState.variantId),
        drawerState.addToCartLabel,
      );
    }
  }
}

function closeDrawer() {
  if (!drawerEl) return;
  hideRecipeDetail();
  setDrawerCartBusy(false);
  drawerEl.classList.remove("is-open");
  drawerEl.setAttribute("aria-hidden", "true");
}

async function openDrawer(payload) {
  if (
    !drawerEl ||
    payload?.uniqueId === undefined ||
    payload?.uniqueId === null ||
    payload?.uniqueId === ""
  ) {
    return;
  }

  drawerState = {
    uniqueId: payload.uniqueId,
    wineKey: payload.wineKey || "",
    variantId: payload.variantId || "",
    foodPairingsTaskId: payload.foodPairingsTaskId || "",
    addToCartLabel: payload.addToCartLabel || "Add to Basket",
    foodPairingsLoaded: false,
    foodPairingsLoading: false,
    wineTitle: "",
  };

  const tastingPane = q('[data-drawer-pane="tasting-notes"]');
  const foodPane = q('[data-drawer-pane="food-pairings"]');
  if (tastingPane) tastingPane.innerHTML = loadingHtml();

  const initialTab = payload.initialTab || "tasting-notes";
  const resumeRecipeTitle = initialTab === "recipe" ? (payload.recipeTitle || "") : "";
  if (foodPane) foodPane.innerHTML = resumeRecipeTitle ? loadingHtml("Loading recipe…") : "";

  setTab(initialTab === "recipe" ? "food-pairings" : initialTab);
  drawerEl.classList.add("is-open");
  drawerEl.setAttribute("aria-hidden", "false");
  if(payload.ctaLink){
    renderFooterCTA(payload.ctaLink);
  } else {
    renderFooter(
      drawerState.variantId,
      payload.initialQty ?? cartQtyForVariant(drawerState.variantId),
      drawerState.addToCartLabel,
    );
  }

  try {
    const card = await api.getCardDetails(payload.uniqueId);

    // Resolve wine_key / variant for food-pairings API (may come from card, not carousel).
    drawerState.wineKey =
      drawerState.wineKey ||
      String(card.variant_id || card.wine_key || card["Variant SKU"] || card.sku || "");
    const resolvedVariantId = String(card.variant_id || card["Variant SKU"] || card.sku || "");
    const hadVariantId = !!drawerState.variantId;
    drawerState.variantId = drawerState.variantId || resolvedVariantId;

    // The very first renderFooter() call above ran with whatever variantId the
    // carousel-level wine object carried — for tenants whose SKU only arrives
    // via this card-details fetch (not upfront on the carousel card), that
    // call's variantId was empty and renderFooter's own !variantId guard bailed
    // out, leaving the footer permanently blank. Now that the real id is known,
    // render it for real (skip the CTA-link/"Buy Now" tenants — unaffected).
    if (!hadVariantId && drawerState.variantId && !payload.ctaLink) {
      renderFooter(
        drawerState.variantId,
        cartQtyForVariant(drawerState.variantId),
        drawerState.addToCartLabel,
      );
    }

    const img = q(".ws-wine-drawer-product-image");
    const titleEl = q(".ws-wine-drawer-product-title");
    if (img) {
      img.src = card.cover_image_link || "";
      img.alt = card.title || "";
    }
    if (titleEl) titleEl.textContent = card.title || "";
    drawerState.wineTitle = card.title || "";

    const compareEl = q(".ws-wine-drawer-compare-price");
    const regularEl = q(".ws-wine-drawer-regular-price");
    if (regularEl) regularEl.textContent = card.regular_price || "";
    const compare = card.compare_at_price || "";
    if (compareEl) {
      if (compare && compare !== card.regular_price) {
        compareEl.textContent = compare;
        compareEl.style.display = "";
      } else {
        compareEl.textContent = "";
        compareEl.style.display = "none";
      }
    }

    if (tastingPane) {
      const notesPayload = buildDrawerNotesPayload(card);
      tastingPane.innerHTML =
        buildTastingPaneHtml(notesPayload) ||
        `<p class="ws-drawer-error-text">No tasting notes available.</p>`;
    }
  } catch (err) {
    console.warn("[RedConcierge] card details failed", err);
    if (tastingPane) {
      tastingPane.innerHTML = `<p class="ws-drawer-error-text">Could not load wine details.</p>`;
    }
  }

  if (initialTab === "food-pairings" || initialTab === "recipe") {
    if (resumeRecipeTitle) {
      const pane = q('[data-drawer-pane="food-pairings"]');
      q(".ws-wine-drawer-tabs")?.style && (q(".ws-wine-drawer-tabs").style.display = "none");
      q(".ws-wine-drawer-product")?.style && (q(".ws-wine-drawer-product").style.display = "none");
      const titleEl = q("#ws-drawer-title");
      if (titleEl) titleEl.textContent = "Recipes";

      const cached = fpCacheGet(drawerState.foodPairingsTaskId, drawerState.wineKey);
      if (cached?.status === "completed") {
        if (pane) pane.innerHTML = buildFoodPairingsHtml(cached);
        drawerState.foodPairingsLoaded = true;
        const all = allPairings(cached);
        const idx = all.findIndex(
          (p) => (p.title || "").toLowerCase() === resumeRecipeTitle.toLowerCase(),
        );
        if (idx >= 0) showRecipeDetail(idx);
      } else {
        if (pane) pane.innerHTML = loadingHtml("Loading recipe…");
        loadFoodPairings();
      }
    } else {
      loadFoodPairings();
    }
  }
}

function prefetchRecipes(taskId, wineKey, data) {
  const pairings = data?.pairings || data?.dishes || [];
  if (!taskId || !wineKey || !pairings.length) return;
  const sessionId = getState().session?.id || "";
  pairings.forEach((item) => {
    if (typeof item === "object" && item.title) {
      api.getFoodPairingRecipe({ taskId, wineKey, dishTitle: item.title, sessionId })
        .catch(() => {});
    }
  });
}

async function loadFoodPairings() {
  if (!drawerEl) return;

  const taskId =
    drawerState.foodPairingsTaskId || getState().session?.foodPairingsTaskId || "";
  const wineKey = drawerState.wineKey || "";
  drawerState.foodPairingsTaskId = taskId;

  if (drawerState.foodPairingsLoaded || drawerState.foodPairingsLoading) return;
  drawerState.foodPairingsLoading = true;
  const pane = q('[data-drawer-pane="food-pairings"]');
  if (!pane) {
    drawerState.foodPairingsLoading = false;
    return;
  }

  if (!taskId || !wineKey) {
    pane.innerHTML = `<p class="ws-drawer-error-text">Food pairings are not available for this wine.</p>`;
    drawerState.foodPairingsLoaded = true;
    drawerState.foodPairingsLoading = false;
    return;
  }

  const cached = fpCacheGet(taskId, wineKey);
  if (cached?.status === "completed") {
    pane.innerHTML = buildFoodPairingsHtml(cached);
    drawerState.foodPairingsLoaded = true;
    drawerState.foodPairingsLoading = false;
    prefetchRecipes(taskId, wineKey, cached);
    return;
  }

  pane.innerHTML = loadingHtml("Generating pairings…");

  const sessionId = getState().session?.id || (await ensureSession()) || "";
  let attempts = 0;
  const maxAttempts = 12;
  const interval = 1500;

  const poll = () => {
    if (!drawerEl?.classList.contains("is-open") || drawerState.wineKey !== wineKey) return;

    api.getFoodPairings({ taskId, wineKey, sessionId })
      .then((data) => {
        if (data.status === "completed") {
          fpCacheSet(taskId, wineKey, data);
          pane.innerHTML = buildFoodPairingsHtml(data);
          drawerState.foodPairingsLoaded = true;
          drawerState.foodPairingsLoading = false;
          prefetchRecipes(taskId, wineKey, data);
        } else if (data.status === "failed") {
          pane.innerHTML = `<div class="ws-drawer-loading"><p class="ws-drawer-loading-text">Couldn't load pairings.</p><button type="button" class="ws-drawer-retry-btn" data-drawer-retry-fp>Retry</button></div>`;
          drawerState.foodPairingsLoaded = true;
          drawerState.foodPairingsLoading = false;
        } else if (++attempts < maxAttempts) {
          // pending | in_progress | generating — keep polling
          setTimeout(poll, interval);
        } else {
          pane.innerHTML = `<div class="ws-drawer-loading"><p class="ws-drawer-loading-text">Pairings are taking longer than expected.</p><button type="button" class="ws-drawer-retry-btn" data-drawer-retry-fp>Retry</button></div>`;
          drawerState.foodPairingsLoaded = true;
          drawerState.foodPairingsLoading = false;
        }
      })
      .catch(() => {
        if (++attempts < maxAttempts) setTimeout(poll, interval);
        else drawerState.foodPairingsLoading = false;
      });
  };
  poll();
}

function hideRecipeDetail() {
  const pane = q('[data-drawer-pane="food-pairings"]');
  if (!pane) return;

  pane.querySelectorAll(".ws-drawer-fp-grid, .ws-drawer-fp-blurb, .ws-drawer-fp-heading, .ws-drawer-fp-previous").forEach((el) => {
    el.style.display = "";
  });
  const prevLabel = pane.querySelector(".ws-drawer-fp-previous-title");
  if (prevLabel) prevLabel.style.display = "";

  const detail = pane.querySelector(".ws-drawer-recipe-detail");
  if (detail) {
    detail.classList.remove("is-visible");
    detail.innerHTML = "";
    detail.style.display = "none";
  }

  q(".ws-wine-drawer-tabs")?.style && (q(".ws-wine-drawer-tabs").style.display = "");
  q(".ws-wine-drawer-product")?.style && (q(".ws-wine-drawer-product").style.display = "");
  const title = q("#ws-drawer-title");
  if (title) title.textContent = "Food Pairings";
}

function renderRecipeDetail(detailEl, pairing, recipeData) {
  const recipeBlock = recipeData.recipe || {};
  const ingredients = recipeBlock.ingredients || [];
  const instructions = recipeBlock.instructions || [];
  const whyItPairs = recipeData.why_it_pairs || pairing.why_it_pairs || "";
  const recipeTitle = recipeData.title || pairing.title || "";

  let html = `<div class="ws-drawer-recipe-header-row">
    <div data-action="recipe-back" class="ws-drawer-recipe-header-left">
      <span class="ws-drawer-recipe-back">‹</span>
      <h4 class="ws-drawer-recipe-title">${escapeHtml(recipeTitle)}</h4>
    </div>
    <div class="ws-drawer-recipe-actions">
      <button type="button" class="ws-drawer-recipe-action-btn" data-drawer-recipe-download aria-label="Download">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      </button>
    </div>
  </div>`;

  if (pairing.descriptor) {
    html += `<p class="ws-drawer-recipe-descriptor">${escapeHtml(pairing.descriptor)}</p>`;
  }
  if (ingredients.length) {
    html += `<h4 class="ws-drawer-recipe-section">Ingredients</h4><div class="ws-drawer-ingredients">`;
    ingredients.forEach((ing) => {
      if (typeof ing === "string") {
        html += `<div class="ws-drawer-ingredient-row"><span class="ws-drawer-ingredient-name"><span class="ws-drawer-ingredient-bullet">•</span>${escapeHtml(ing)}</span></div>`;
      } else if (ing && typeof ing === "object") {
        const name = ing.name || "";
        const amount = ing.amount || "";
        html += `<div class="ws-drawer-ingredient-row">
          <span class="ws-drawer-ingredient-name"><span class="ws-drawer-ingredient-bullet">•</span>${escapeHtml(name || amount)}</span>
          ${amount && name ? `<span class="ws-drawer-ingredient-amount">${escapeHtml(amount)}</span>` : ""}
        </div>`;
      }
    });
    html += `</div>`;
  }
  if (instructions.length) {
    html += `<h4 class="ws-drawer-recipe-section">Method</h4><ol class="ws-drawer-method-list">`;
    instructions.forEach((step, i) => {
      html += `<li class="ws-drawer-method-step"><span class="ws-drawer-method-num">${i + 1}</span><span>${escapeHtml(String(step).replace(/^step\s*\d+[:.]\s*/i, ""))}</span></li>`;
    });
    html += `</ol>`;
  }
  if (whyItPairs) {
    html += `<div class="ws-drawer-pairing-box"><p class="ws-drawer-pairing-box-title">Why it pairs</p><p class="ws-drawer-pairing-box-text">${escapeHtml(whyItPairs)}</p></div>`;
  }
  detailEl.innerHTML = html;

  const pane = detailEl.closest(".ws-wine-drawer-pane");
  const headerRow = detailEl.querySelector(".ws-drawer-recipe-header-row");
  if (pane && headerRow) {
    pane.addEventListener("scroll", () => {
      headerRow.classList.toggle("is-scrolled", pane.scrollTop > 10);
    }, { passive: true });
  }
}

async function showRecipeDetail(index) {
  const { foodPairingsTaskId: taskId, wineKey } = drawerState;
  const cached = fpCacheGet(taskId, wineKey);
  if (!cached?.pairings) return;
  const pairing = allPairings(cached)[index];
  if (!pairing) return;

  const pane = q('[data-drawer-pane="food-pairings"]');
  if (!pane) return;

  pane.querySelectorAll(".ws-drawer-fp-grid, .ws-drawer-fp-blurb, .ws-drawer-fp-heading").forEach((el) => {
    el.style.display = "none";
  });
  const prevLabel = pane.querySelector(".ws-drawer-fp-previous-title");
  if (prevLabel) prevLabel.style.display = "none";

  q(".ws-wine-drawer-tabs")?.style && (q(".ws-wine-drawer-tabs").style.display = "none");
  q(".ws-wine-drawer-product")?.style && (q(".ws-wine-drawer-product").style.display = "none");
  const title = q("#ws-drawer-title");
  if (title) title.textContent = "Recipes";

  const detail = pane.querySelector(".ws-drawer-recipe-detail");
  if (!detail) return;
  detail.style.display = "block";
  detail.classList.add("is-visible");
  detail.innerHTML = `<div data-action="recipe-back" class="ws-drawer-recipe-header-left" style="margin-bottom:8px;padding:8px 0;">
    <span class="ws-drawer-recipe-back">‹</span>
    <h4 class="ws-drawer-recipe-title">${escapeHtml(pairing.title)}</h4>
  </div>${loadingHtml("Loading recipe…")}`;

  const sessionId = getState().session?.id || (await ensureSession()) || "";
  if (cached) {
    if (!cached._recipe_clicked) cached._recipe_clicked = [];
    if (!cached._recipe_clicked.includes(pairing.title)) {
      cached._recipe_clicked.push(pairing.title);
      fpCacheSet(taskId, wineKey, cached);
      api.trackRecipeClick({ sessionId, wineKey, dishTitle: pairing.title }).catch(() => {});
    }
  }

  let attempts = 0;
  const poll = () => {
    api.getFoodPairingRecipe({ taskId, wineKey, dishTitle: pairing.title, sessionId })
      .then((data) => {
        if (isRecipeReady(data)) {
          renderRecipeDetail(detail, pairing, data);
        } else if (data.status === "failed") {
          pane.innerHTML = `<p class="ws-drawer-error-text">Could not load recipe.</p>`;
        } else if (++attempts < 10) {
          setTimeout(poll, 2000);
        }
      })
      .catch(() => {
        if (++attempts < 10) setTimeout(poll, 2000);
      });
  };
  poll();
}

async function handleRecipeRefresh() {
  const { foodPairingsTaskId: taskId, wineKey } = drawerState;
  const pane = q('[data-drawer-pane="food-pairings"]');
  if (!pane || !taskId || !wineKey) return;

  const oldCached = fpCacheGet(taskId, wineKey);
  const previousPairings = oldCached
    ? [...(oldCached._previousPairings || []), ...(oldCached.pairings || [])]
    : [];

  pane.innerHTML = loadingHtml("Generating new pairings…");
  try {
    sessionStorage.removeItem(fpCacheKey(taskId, wineKey));
  } catch {
    /* ignore */
  }
  drawerState.foodPairingsLoaded = false;
  drawerState.foodPairingsLoading = false;

  const sessionId = getState().session?.id || (await ensureSession()) || "";
  try {
    await api.getFoodPairings({ taskId, wineKey, sessionId, refresh: true });
  } catch {
    /* generation may still proceed */
  }

  let attempts = 0;
  const poll = () => {
    if (!drawerEl?.classList.contains("is-open") || drawerState.wineKey !== wineKey) return;
    api.getFoodPairings({ taskId, wineKey, sessionId })
      .then((data) => {
        if (data.status === "completed") {
          data._previousPairings = previousPairings;
          fpCacheSet(taskId, wineKey, data);
          pane.innerHTML = buildFoodPairingsHtml(data);
          drawerState.foodPairingsLoaded = true;
          prefetchRecipes(taskId, wineKey, data);
        } else if (++attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          pane.innerHTML = `<p class="ws-drawer-error-text">Could not load new pairings. Please try again.</p>`;
          drawerState.foodPairingsLoaded = true;
        }
      })
      .catch(() => {
        if (++attempts < 15) setTimeout(poll, 2000);
      });
  };
  setTimeout(poll, 2000);
}

/** Load an image URL as a data URL so html2pdf/html2canvas can embed it. */
async function imageUrlToDataUrl(url) {
  if (!url) return "";
  if (String(url).startsWith("data:")) return url;

  // Prefer fetch → blob (more reliable than img+canvas for CORS assets)
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (res.ok) {
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    /* fall through to Image canvas */
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 64;
        canvas.height = img.naturalHeight || 64;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve("");
      }
    };
    img.onerror = () => resolve("");
    img.src = url;
  });
}

function printLogoImgHtml(dataUrl, alt) {
  if (!dataUrl) return "";
  return `<img class="print-footer-logo" src="${dataUrl}" width="14" height="14" alt="${escapeHtml(alt)}" style="border-radius:50%;object-fit:cover;display:inline-block;vertical-align:middle;">`;
}

/** html2canvas is unreliable with webp data-URLs — normalize logos to PNG. */
async function ensurePngDataUrl(url) {
  const raw = await imageUrlToDataUrl(url);
  if (!raw) return "";
  if (raw.startsWith("data:image/png")) return raw;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 64;
        canvas.height = img.naturalHeight || 64;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(raw);
      }
    };
    img.onerror = () => resolve(raw);
    img.src = raw;
  });
}

/** Light accents (Goose gold, etc.) are unreadable on white — darken footer brand. */
function footerBrandColor(accentHex) {
  const c = parseCssColor(accentHex);
  if (!c) return "#333333";
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return lum > 0.55 ? "#333333" : toHex(c);
}

/** Parse #rgb / #rrggbb / rgb()/rgba() → {r,g,b} or null. */
function parseCssColor(input) {
  if (!input || typeof input !== "string") return null;
  const s = input.trim();
  let m = s.match(/^#([0-9a-f]{3})$/i);
  if (m) {
    const [r, g, b] = m[1].split("").map((c) => parseInt(c + c, 16));
    return { r, g, b };
  }
  m = s.match(/^#([0-9a-f]{6})$/i);
  if (m) {
    return {
      r: parseInt(m[1].slice(0, 2), 16),
      g: parseInt(m[1].slice(2, 4), 16),
      b: parseInt(m[1].slice(4, 6), 16),
    };
  }
  m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (m) {
    return { r: Math.round(+m[1]), g: Math.round(+m[2]), b: Math.round(+m[3]) };
  }
  return null;
}

function toHex({ r, g, b }) {
  const h = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Mix color toward white (amount 0 = original, 1 = white). */
function mixWithWhite(hexOrRgb, amount) {
  const c = parseCssColor(hexOrRgb);
  if (!c) return "#f5f5f5";
  const mix = (ch) => Math.round(ch + (255 - ch) * amount);
  return toHex({ r: mix(c.r), g: mix(c.g), b: mix(c.b) });
}

/**
 * Accent for recipe PDF — match the live agent drawer colors for this winery.
 * Prefer computed styles from the open recipe detail (Goose overrides method-num
 * / amounts to secondary accent); fall back to theme primary / card accents.
 */
function resolveRecipePrintAccent(detail) {
  const sample =
    detail?.querySelector(".ws-drawer-method-num") ||
    detail?.querySelector(".ws-drawer-ingredient-amount") ||
    detail?.querySelector(".ws-drawer-recipe-section") ||
    detail?.querySelector(".ws-drawer-recipe-title");

  if (sample) {
    try {
      const cs = getComputedStyle(sample);
      const bg = cs.backgroundColor;
      const color = cs.color;
      if (
        sample.classList.contains("ws-drawer-method-num") &&
        bg &&
        bg !== "transparent" &&
        bg !== "rgba(0, 0, 0, 0)"
      ) {
        const parsed = parseCssColor(bg);
        if (parsed) return toHex(parsed);
      }
      if (color && color !== "rgba(0, 0, 0, 0)") {
        const parsed = parseCssColor(color);
        if (parsed) return toHex(parsed);
      }
    } catch {
      /* fall through */
    }
  }

  return (
    getTheme("drawer_heading_color", null) ||
    getTheme("card_accent_color", null) ||
    getTheme("accent_color", null) ||
    getTheme("primary_color", null) ||
    "#691b38"
  );
}

/** Ingredient label without the decorative bullet (avoids PDF "••" double dots). */
function ingredientNameText(row) {
  const nameEl = row.querySelector(".ws-drawer-ingredient-name");
  if (!nameEl) return "";
  const clone = nameEl.cloneNode(true);
  clone.querySelector(".ws-drawer-ingredient-bullet")?.remove();
  return clone.textContent.trim();
}

async function downloadRecipePdf() {
  const detail = q(".ws-drawer-recipe-detail.is-visible");
  if (!detail) return;

  const recipeTitle =
    detail.querySelector(".ws-drawer-recipe-title")?.textContent?.trim() || "Recipe";
  const recipeDescriptor =
    detail.querySelector(".ws-drawer-recipe-descriptor")?.textContent?.trim() || "";

  // Build print HTML with template class names (parity: main.js downloadRecipeAsPDF)
  const ingredientsEl = detail.querySelector(".ws-drawer-ingredients");
  let ingredientsHtml = "";
  if (ingredientsEl) {
    const rows = [...ingredientsEl.querySelectorAll(".ws-drawer-ingredient-row")]
      .map((row) => {
        const name = ingredientNameText(row);
        const amount =
          row.querySelector(".ws-drawer-ingredient-amount")?.textContent?.trim() || "";
        if (!name && !amount) return "";
        return `<div class="recipe-ingredient-row">
          <span class="recipe-ingredient-name"><span class="recipe-ingredient-bullet">•</span>${escapeHtml(name || amount)}</span>
          ${amount && name ? `<span class="recipe-ingredient-amount">${escapeHtml(amount)}</span>` : ""}
        </div>`;
      })
      .filter(Boolean)
      .join("");
    if (rows) {
      ingredientsHtml = `<h4 class="recipe-section-title">Ingredients</h4><div class="recipe-ingredients-list">${rows}</div>`;
    }
  }

  const methodEl = detail.querySelector(".ws-drawer-method-list");
  let methodHtml = "";
  if (methodEl) {
    const steps = [...methodEl.querySelectorAll(".ws-drawer-method-step")]
      .map((step, i) => {
        const num =
          step.querySelector(".ws-drawer-method-num")?.textContent?.trim() ||
          String(i + 1);
        const text = [...step.querySelectorAll("span")]
          .filter((s) => !s.classList.contains("ws-drawer-method-num"))
          .map((s) => s.textContent.trim())
          .join(" ")
          .trim();
        return `<li class="recipe-method-step"><span class="recipe-method-step-num">${escapeHtml(num)}</span><span>${escapeHtml(text)}</span></li>`;
      })
      .join("");
    if (steps) {
      methodHtml = `<h4 class="recipe-section-title">Method</h4><ol class="recipe-method-list">${steps}</ol>`;
    }
  }

  const pairingEl = detail.querySelector(".ws-drawer-pairing-box");
  let pairingHtml = "";
  if (pairingEl) {
    const boxTitle =
      pairingEl.querySelector(".ws-drawer-pairing-box-title")?.textContent?.trim() ||
      "Why it pairs";
    const boxText =
      pairingEl.querySelector(".ws-drawer-pairing-box-text")?.textContent?.trim() || "";
    pairingHtml = `<div class="recipe-pairing-box">
      <p class="recipe-pairing-box-title">${escapeHtml(boxTitle)}</p>
      <p class="recipe-pairing-box-text">${escapeHtml(boxText)}</p>
    </div>`;
  }

  const formattedDescriptor = recipeDescriptor
    ? `<p class="recipe-detail-descriptor">${escapeHtml(recipeDescriptor)}</p>`
    : "";

  // Footer branding (all wineries):
  //   wineengine / thewineengine → "the wine engine"
  //   everyone else              → "RedConcierge"
  const tenantId = String(getTenantId() || getState().tenant?.tenant_id || "").toLowerCase();
  const isWineEngine = tenantId === "wineengine" || tenantId === "thewineengine";

  const poweredBrand = isWineEngine ? "the wine engine" : brandWordmarkHtml;
  const poweredBrandUrl = isWineEngine
    ? "https://www.redaugment.com/products/redconcierge"
    : "https://www.redaugment.com/products/redconcierge";
  const logoSrc = isWineEngine ? wineEngineLogoImg : redAugmentLogo;
  const logoPng = await ensurePngDataUrl(logoSrc);
  const poweredLogoHtml = isWineEngine ? printLogoImgHtml(
    logoPng,
    isWineEngine ? "the wine engine" : "RedConcierge",
  ) : `<img src="${logoPng}" alt="Red Augment" width="24" height="24" style="object-fit:contain;vertical-align:middle;">`;

  // Winery accent — matches agent recipe UI (not hardcoded Wine Engine maroon).
  const accent = resolveRecipePrintAccent(detail);
  const accentSoftBg = mixWithWhite(accent, 0.92);
  const accentSoftBorder = mixWithWhite(accent, 0.72);
  const accentBullet = mixWithWhite(accent, 0.45);
  const footerBrand = footerBrandColor(accent);

  const printHtml = String(recipePrintTemplate)
    .replace(/{{RECIPE_TITLE}}/g, escapeHtml(recipeTitle))
    .replace(/{{RECIPE_DESCRIPTOR}}/g, formattedDescriptor)
    .replace(/{{INGREDIENTS_HTML}}/g, ingredientsHtml)
    .replace(/{{METHOD_HTML}}/g, methodHtml)
    .replace(/{{PAIRING_HTML}}/g, pairingHtml)
    .replace(/{{POWERED_BRAND}}/g, poweredBrand)
    .replace(/{{POWERED_BRAND_URL}}/g, escapeHtml(poweredBrandUrl))
    .replace(/{{POWERED_LOGO_HTML}}/g, poweredLogoHtml)
    .replace(/{{ACCENT_COLOR}}/g, accent)
    .replace(/{{ACCENT_SOFT_BG}}/g, accentSoftBg)
    .replace(/{{ACCENT_SOFT_BORDER}}/g, accentSoftBorder)
    .replace(/{{ACCENT_BULLET}}/g, accentBullet)
    .replace(/{{FOOTER_BRAND_COLOR}}/g, footerBrand)
    .replace(/{{CURRENT_URL}}/g, "");

  const fileName =
    `${recipeTitle.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}_Recipe.pdf`;

  const doGenerate = () => {
    window
      .html2pdf()
      .set({
        margin: [12, 12, 16, 12],
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(printHtml, "string")
      .save();
  };

  if (typeof window.html2pdf === "undefined") {
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js";
    s.onload = doGenerate;
    s.onerror = () => console.warn("[RedConcierge] html2pdf failed to load");
    document.head.appendChild(s);
  } else {
    doGenerate();
  }
}

function mountDrawer(panelEl, shadow) {
  const style = document.createElement("style");
  style.textContent = wineDrawerCSS;
  shadow.appendChild(style);

  drawerEl = document.createElement("div");
  drawerEl.id = "ws-wine-drawer";
  drawerEl.className = "ws-wine-drawer";
  drawerEl.setAttribute("aria-hidden", "true");
  drawerEl.innerHTML = `
    <div class="ws-wine-drawer-sheet">
      <button type="button" class="ws-wine-drawer-close-fab" data-drawer-close aria-label="Close">${ICON_CLOSE}</button>
      <div class="ws-wine-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="ws-drawer-title">
        <div class="ws-wine-drawer-header">
          <span class="ws-wine-drawer-header-icon" id="ws-drawer-header-icon" aria-hidden="true">${ICON_TASTING}</span>
          <h2 id="ws-drawer-title">Tasting Notes</h2>
        </div>
        <div class="ws-wine-drawer-product">
          <div class="ws-wine-drawer-image-wrap">
            <img class="ws-wine-drawer-product-image" src="" alt="">
            <span class="ws-wine-drawer-product-badge"></span>
          </div>
          <div class="ws-wine-drawer-product-info">
            <p class="ws-wine-drawer-product-title"></p>
            <div class="ws-wine-drawer-pricing">
              <span class="ws-wine-drawer-compare-price"></span>
              <span class="ws-wine-drawer-regular-price"></span>
            </div>
          </div>
        </div>
        <div class="ws-wine-drawer-body">
          <div class="ws-wine-drawer-tabs" role="tablist" aria-label="Wine detail sections">
            <button type="button" class="ws-wine-drawer-tab ws-drawer-tab-collapse" aria-label="Toggle menu">
              <span class="ws-drawer-tab-icon"><svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true"><path d="M11.1667 0.5V16.5M6.72222 11.1667L4.05556 8.5L6.72222 5.83333M2.27778 0.5H14.7222C15.7041 0.5 16.5 1.29594 16.5 2.27778V14.7222C16.5 15.7041 15.7041 16.5 14.7222 16.5H2.27778C1.29594 16.5 0.5 15.7041 0.5 14.7222V2.27778C0.5 1.29594 1.29594 0.5 2.27778 0.5Z" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
            </button>
            <button type="button" class="ws-wine-drawer-tab active" data-drawer-tab="tasting-notes" role="tab" aria-selected="true">
              <span class="ws-drawer-tab-icon">${ICON_TASTING}</span><span>Tasting Notes</span>
            </button>
            <button type="button" class="ws-wine-drawer-tab" data-drawer-tab="food-pairings" role="tab" aria-selected="false">
              <span class="ws-drawer-tab-icon">${ICON_FOOD}</span><span>Food Pairings</span>
            </button>
          </div>
          <div class="ws-wine-drawer-content">
            <div class="ws-wine-drawer-pane active" data-drawer-pane="tasting-notes" role="tabpanel"></div>
            <div class="ws-wine-drawer-pane" data-drawer-pane="food-pairings" role="tabpanel"></div>
          </div>
        </div>
        <div class="ws-wine-drawer-footer"></div>
      </div>
    </div>`;

  panelEl.appendChild(drawerEl);

  drawerEl.addEventListener("click", (e) => {
    if (e.target.closest("[data-drawer-close]")) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    const tab = e.target.closest("[data-drawer-tab]");
    if (tab && tab.dataset.drawerTab) {
      e.preventDefault();
      e.stopPropagation();
      const name = tab.dataset.drawerTab;
      setTab(name);
      if (name === "food-pairings") loadFoodPairings();
      return;
    }

    if (e.target.closest(".ws-drawer-tab-collapse")) {
      q(".ws-wine-drawer-tabs")?.classList.toggle("collapsed");
      return;
    }

    const fpCard = e.target.closest(".ws-drawer-fp-card[data-pairing-index]");
    if (fpCard) {
      e.preventDefault();
      showRecipeDetail(parseInt(fpCard.dataset.pairingIndex, 10));
      return;
    }

    if (e.target.closest("[data-action='recipe-back']")) {
      e.preventDefault();
      hideRecipeDetail();
      return;
    }

    if (e.target.closest("[data-action='recipe-refresh']")) {
      e.preventDefault();
      handleRecipeRefresh();
      return;
    }

    if (e.target.closest("[data-drawer-retry-fp]")) {
      e.preventDefault();
      drawerState.foodPairingsLoaded = false;
      drawerState.foodPairingsLoading = false;
      loadFoodPairings();
      return;
    }

    if (e.target.closest("[data-drawer-recipe-download]")) {
      e.preventDefault();
      downloadRecipePdf();
      return;
    }

    const cartBtn = e.target.closest("[data-drawer-cart]");
    if (cartBtn) {
      e.preventDefault();
      const variantId = cartBtn.dataset.variantId;
      if (!variantId) return;
      const action = cartBtn.dataset.drawerCart;
      const footer = cartBtn.closest(".ws-wine-drawer-footer") || q(".ws-wine-drawer-footer");

      // Old: overlayLoading(footer) before addToCart / updateQty / delete.
      setDrawerCartBusy(true);
      if (!drawerFooterOverlay) drawerFooterOverlay = overlayLoading(footer);

      // fromDrawer: old tasting-notes never called showCartsProducts — no
      // "Here is your order summary"; keep drawer open, refresh footer/badge.
      // Commerce7 tenants' cart feature (cart_c7.js) listens for {sku}, not
      // {variantId} — same identifier value, different key per tenant.
      const idKey = isCommerce7Tenant() ? "sku" : "variantId";
      if (action === "add") {
        emit("ws-cart-add", { [idKey]: variantId, quantity: 1, fromDrawer: true });
      } else if (action === "remove") {
        emit("ws-cart-remove", { [idKey]: variantId, fromDrawer: true });
      } else if (action === "inc") {
        const qty = cartQtyForVariant(variantId);
        emit("ws-cart-update", {
          [idKey]: variantId,
          quantity: qty + 1,
          fromDrawer: true,
        });
      } else if (action === "dec") {
        const qty = cartQtyForVariant(variantId);
        if (qty > 1) {
          emit("ws-cart-update", {
            [idKey]: variantId,
            quantity: qty - 1,
            fromDrawer: true,
          });
        } else {
          setDrawerCartBusy(false);
        }
      } else {
        setDrawerCartBusy(false);
      }
    }
  });
}

export function init({ shadow } = {}) {
  const panel = shadow?.querySelector?.(".ws-panel");
  if (!panel) {
    console.warn("[RedConcierge] wine-drawer: .ws-panel not found");
    return;
  }
  mountDrawer(panel, shadow);

  on("ws-wine-drawer-open", (payload) => openDrawer(payload));
  on("ws-cart-count", () => syncFooterIfOpen());
  // Cleared like old removeOverlayLoading in the cart mutation finally block.
  on("ws-cart-busy", ({ active } = {}) => {
    if (!active) setDrawerCartBusy(false);
  });
}

function isDrawerOpen() {
  return !!drawerEl?.classList.contains("is-open");
}

function getActiveDrawerContext() {
  if (!isDrawerOpen()) return null;

  const activeTab = drawerEl?.querySelector("[data-drawer-tab].active");
  const tabName = activeTab?.dataset?.drawerTab || "tasting-notes";

  const recipeDetail = drawerEl?.querySelector(".ws-drawer-recipe-detail.is-visible");
  let tab = tabName;
  let recipeTitle = "";
  if (recipeDetail) {
    tab = "recipe";
    recipeTitle = recipeDetail.querySelector(".ws-drawer-recipe-title")?.textContent?.trim() || "";
  }

  return {
    uniqueId: drawerState.uniqueId || "",
    wineKey: drawerState.wineKey || "",
    wineTitle: drawerState.wineTitle || "",
    variantId: drawerState.variantId || "",
    foodPairingsTaskId: drawerState.foodPairingsTaskId || "",
    tab,
    recipeTitle,
  };
}

export { closeDrawer as closeWineDrawer, getDrawer, isDrawerOpen, getActiveDrawerContext };
